import { apiFetch } from '@/lib/apiFetch';
import { handleUnauthorized } from './UserManagmentServices';
import type { TeacherTrialResponse } from './teacherSubscriptionService';

// F15 — GET /api/v1/teacher/entitlements: the teacher's resolved offering-type
// and publish-count limits, current usage, and grace-period state. Backs the
// proactive (pre-click) publish-restriction UI in CreateOfferingWizard.tsx and
// ViewOfferingPage.tsx — the 409 from POST /publish remains the source of
// truth; this is best-effort, ahead-of-time context only.

export interface TeacherEntitlementGraceResponse {
  active: boolean;
  deadline: string;
  excessOfferingIds: string[];
}

export interface TeacherEntitlementsResponse {
  /** null when the teacher has no active subscription (see also `source`). */
  tier: string | null;
  /**
   * Where the resolved entitlements come from: `SUBSCRIPTION`, `NO_SUBSCRIPTION`, or `TRIAL` for a
   * teacher on the Platinum free trial (tier resolves to PLATINUM without a paid subscription).
   */
  source: string;
  allowedOfferingTypes: string[];
  maxPublishedOfferings: number;
  currentPublishedOfferings: number;
  bonusCoinsPerMonth: number;
  coinDiscountPct: number;
  effectiveReplyCost: number;
  /** null when the teacher isn't over their plan's limits. */
  grace: TeacherEntitlementGraceResponse | null;
  /**
   * The teacher's Platinum free trial, or null/absent when they have none. This is the gate the
   * trial UI reads: it is server truth, so it stays correct with any release flag off, and it
   * falls back to null the moment the trial expires (entitlements return to NO_SUBSCRIPTION).
   */
  trial?: TeacherTrialResponse | null;
}

export async function fetchTeacherEntitlements(accessToken: string): Promise<TeacherEntitlementsResponse> {
  const response = await apiFetch('/api/v1/teacher/entitlements', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to fetch teacher entitlements: ${response.status}`);
  return response.json();
}
